import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import {
  Snowflake, AirVent, Wrench, Wind,
  CheckCircle2, ArrowRight, PhoneCall,
  ShieldCheck, Zap, Users, Clock,
  ChevronDown, Mail, Phone,
  MoveRight,
} from 'lucide-react';
import Navbar from './Navbar';
import Contact from './Contact';
import Footer from './Footer';
import './Solution.css';

import imgClim   from '../assets/climService.jpg';
import imgPompe  from '../assets/pompeService.jpg';
import imgVent   from '../assets/ventilation.jpg';
import imgEntret from '../assets/entretientService.jpg';
import logoRGE   from '../assets/logo-qualipac-RGE.PNG';

/* ── DATA ───────────────────────────────────── */
const SOLUTIONS = [
  {
    id: 'climatisation',
    path: '/climatisation',
    num: '01',
    title: 'Climatisation',
    tagline: "Confort thermique toute l'année",
    icon: <Snowflake size={26} strokeWidth={1.6} />,
    color: '#1d4ed8',
    colorLight: 'rgba(29,78,216,0.08)',
    image: imgClim,
    stat: { val: '500+', label: 'Installations réalisées' },
    desc: 'Installation et maintenance de climatiseurs splits, multi-splits et systèmes gainables pour une régulation thermique optimale.',
    detail: 'Nous concevons des installations de climatisation performantes, discrètes et adaptées à votre logement, vos bureaux ou locaux professionnels. Notre objectif : un confort optimal été comme hiver, avec une consommation maîtrisée.',
    items: ['Split mural', 'Multi-split', 'Gainable', 'Cassette plafond', 'Console', 'Étude de dimensionnement'],
    benefits: ["Confort thermique toute l'année", 'Installation propre et discrète', 'Adaptée aux particuliers et professionnels'],
  },
  {
    id: 'pompe-chaleur',
    path: '/pompe-a-chaleur',
    num: '02',
    title: 'Pompe à chaleur',
    tagline: 'Économies et performance énergétique',
    icon: <AirVent size={26} strokeWidth={1.6} />,
    color: '#c8102e',
    colorLight: 'rgba(200,16,46,0.07)',
    image: imgPompe,
    stat: { val: '-70%', label: 'Sur la facture de chauffage' },
    desc: 'Solutions éco-responsables de pompe à chaleur air-air et air-eau pour un chauffage et un rafraîchissement économiques.',
    detail: "Nous vous accompagnons dans l'installation de pompes à chaleur adaptées à votre habitation pour réduire durablement vos dépenses énergétiques. Solutions fiables, évolutives et conformes aux normes.",
    items: ['PAC air-air', 'PAC air-eau', 'Système réversible', 'Plancher chauffant', 'Radiateurs basse température', 'Conseil sur les aides'],
    benefits: ['Réduction de la consommation énergétique', 'Chauffage et rafraîchissement combinés', 'Valorisation durable de votre bien'],
  },
  {
    id: 'ventilation',
    path: '/ventilation',
    num: '03',
    title: 'Ventilation',
    tagline: "Un air intérieur sain et renouvelé",
    icon: <Wind size={26} strokeWidth={1.6} />,
    color: '#0f766e',
    colorLight: 'rgba(15,118,110,0.08)',
    image: imgVent,
    stat: { val: '3×', label: "Renouvellement d'air/jour" },
    desc: "Installation, entretien et remplacement de systèmes de ventilation pour assurer une circulation d'air saine et efficace dans vos espaces.",
    detail: "Nous installons et entretenons des systèmes de ventilation adaptés à votre habitation ou à vos locaux professionnels afin d'améliorer la qualité de l'air intérieur, limiter l'humidité et garantir un renouvellement d'air efficace au quotidien.",
    items: ["VMC simple flux", "VMC double flux", "Ventilation hygroréglable", "Extraction d'air", "Entretien ventilation", "Remplacement de système existant"],
    benefits: ["Amélioration de la qualité de l'air intérieur", "Réduction de l'humidité et des moisissures", 'Solution adaptée aux logements et locaux professionnels'],
  },
  {
    id: 'entretien',
    path: '/entretien-climatisation',
    num: '04',
    title: 'Services & Entretien',
    tagline: 'Maintenance et dépannage réactif',
    icon: <Wrench size={26} strokeWidth={1.6} />,
    color: '#0a2463',
    colorLight: 'rgba(10,36,99,0.07)',
    image: imgEntret,
    stat: { val: '24h', label: 'Délai intervention garanti' },
    desc: 'Entretien annuel, dépannage et réparation toutes marques. Contrats de maintenance pour entreprises et copropriétés.',
    detail: "Nous assurons l'entretien, la maintenance et le dépannage de vos équipements pour préserver leurs performances et leur durée de vie. Visite ponctuelle, contrat annuel ou intervention rapide.",
    items: ['Entretien annuel', 'Dépannage rapide', 'Nettoyage des filtres', 'Contrat de maintenance', 'Vérification performances'],
    benefits: ['Préservation des performances', 'Réduction du risque de panne', 'Suivi sérieux et durable'],
  },
];

const HIGHLIGHTS = [
  { icon: <ShieldCheck size={20} strokeWidth={1.8} />, title: 'Certifié RGE', text: "QualiPAC — accès aux aides de l'État" },
  { icon: <Clock size={20} strokeWidth={1.8} />, title: 'Réactif 24h/24', text: 'Intervention rapide en Île-de-France' },
  { icon: <Zap size={20} strokeWidth={1.8} />, title: 'Expertise prouvée', text: '+500 installations réalisées' },
  { icon: <Users size={20} strokeWidth={1.8} />, title: 'Tous profils', text: 'Particuliers, pros, copropriétés' },
];

const STEPS = [
  { num: '01', title: 'Échange & étude', text: 'Nous analysons votre besoin, vos contraintes et vos objectifs de confort.', icon: <Phone size={22} strokeWidth={1.7} /> },
  { num: '02', title: 'Solution adaptée', text: 'Nous vous proposons une solution claire, cohérente et performante.', icon: <CheckCircle2 size={22} strokeWidth={1.7} /> },
  { num: '03', title: 'Installation', text: 'Nos équipes interviennent avec soin pour une pose propre et durable.', icon: <Wrench size={22} strokeWidth={1.7} /> },
  { num: '04', title: 'Suivi & entretien', text: 'Nous restons à vos côtés pour la maintenance et les dépannages.', icon: <ShieldCheck size={22} strokeWidth={1.7} /> },
];

const FAQ = [
  { q: 'Intervenez-vous pour les particuliers et les professionnels ?', a: 'Oui, nous accompagnons les particuliers, les commerces, les bureaux et les copropriétés selon la nature du projet.' },
  { q: "Proposez-vous l'entretien de systèmes déjà installés ?", a: "Oui, nous proposons l'entretien, le contrôle et le dépannage sur de nombreux équipements existants, toutes marques." },
  { q: 'Pouvez-vous nous conseiller sur la meilleure solution ?', a: "Oui, nous vous guidons selon la surface, l'usage, la configuration des lieux et le niveau de performance recherché." },
  { q: "Avez-vous accès aux aides financières de l'État ?", a: "Oui, notre certification RGE QualiPAC vous donne accès à MaPrimeRénov', CEE, éco-PTZ et TVA réduite à 5,5 %." },
];

/* ── INTERSECTION HOOK ──────────────────────── */
function useInView(threshold = 0.08) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ── SERVICE CARD LARGE ─────────────────────── */
function ServiceRow({ s, index }) {
  const [ref, inView] = useInView();
  const isEven = index % 2 === 0;
  return (
    <div
      ref={ref}
      id={s.id}
      className={`svc-row${isEven ? '' : ' svc-row--flip'}${inView ? ' svc-row--in' : ''}`}
      style={{ '--c': s.color, '--cl': s.colorLight, '--delay': `${index * 0.08}s` }}
    >
      {/* Photo */}
      <div className="svc-row__photo">
        <img src={s.image} alt={s.title} loading="lazy" />
        <div className="svc-row__photo-badge">
          <span className="svc-row__photo-val">{s.stat.val}</span>
          <span className="svc-row__photo-lbl">{s.stat.label}</span>
        </div>
      </div>

      {/* Texte */}
      <div className="svc-row__body">
        <div className="svc-row__top">
          <div className="svc-row__num">{s.num}</div>
          <div className="svc-row__icon">{s.icon}</div>
        </div>

        <h2 className="svc-row__title">{s.title}</h2>
        <p className="svc-row__tag">{s.tagline}</p>
        <p className="svc-row__desc">{s.detail}</p>

        <div className="svc-row__lists">
          <div>
            <p className="svc-row__list-label">Prestations</p>
            <ul className="svc-row__checklist">
              {s.items.map((item, j) => (
                <li key={j}><CheckCircle2 size={13} strokeWidth={2.5} />{item}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="svc-row__list-label">Avantages</p>
            <ul className="svc-row__dotlist">
              {s.benefits.map((b, j) => (
                <li key={j}><span />{b}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="svc-row__actions">
          <a href="#contact" className="svc-row__btn-solid">
            Demander un devis <ArrowRight size={15} strokeWidth={2} />
          </a>
          <a href="tel:0618011895" className="svc-row__btn-outline">
            <PhoneCall size={14} strokeWidth={2} /> 06 18 01 18 95
          </a>
          <Link to={s.path} className="svc-row__btn-more">
            En savoir plus <ArrowRight size={14} strokeWidth={2} />
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ── PAGE ───────────────────────────────────── */
export default function Solutions() {
  const [openFaq, setOpenFaq] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const top = el.getBoundingClientRect().top + window.pageYOffset - 110;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 150);
    } else {
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [location]);

  return (
    <>
      <Navbar />
      <main className="sol-page">

        {/* ══════════════════════════════════════
            HERO — inchangé
        ══════════════════════════════════════ */}
        <section className="sol-hero">
          <div className="sol-hero__bg" />
          <div className="sol-hero__geo" aria-hidden="true">
            <div className="sol-geo-line1" />
            <div className="sol-geo-line2" />
          </div>

          <div className="sol-hero__inner container">
            <div className="sol-hero__left">
              <div className="sol-pill">
                <span className="sol-pill-dot" />
                Installation · Entretien · Dépannage · Île-de-France
              </div>

              <h1 className="sol-hero__title">
                <span className="sol-hl sol-hl--1">DES <em>SOLUTIONS</em></span>
                <span className="sol-hl sol-hl--2">FIABLES</span>
                <span className="sol-hl sol-hl--3">POUR VOTRE CONFORT</span>
              </h1>

              <div className="sol-rule"><b /><i /></div>

              <p className="sol-hero__desc">
                Climatisation, pompe à chaleur, ventilation, entretien et
                dépannage : SOCLIMATE vous accompagne avec des prestations
                sérieuses et adaptées à chaque situation.
              </p>

              <div className="sol-hero__ctas">
                <a href="#solutions-grid" className="sol-btn-primary">
                  Découvrir nos solutions <ArrowRight size={16} strokeWidth={2} />
                </a>
                <a href="tel:0618011895" className="sol-btn-ghost">
                  <Phone size={15} strokeWidth={2} /> 06 18 01 18 95
                </a>
              </div>
            </div>

            <div className="sol-hero__right">
              {HIGHLIGHTS.map((h, i) => (
                <div key={i} className="sol-highlight">
                  <div className="sol-highlight__icon">{h.icon}</div>
                  <div>
                    <strong>{h.title}</strong>
                    <span>{h.text}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="sol-statsbar">
            <div className="sol-stats container">
              <div className="sol-stat"><span className="sol-stat__val">500+</span><span className="sol-stat__lbl">Installations</span></div>
              <div className="sol-stat__sep" />
              <div className="sol-stat"><span className="sol-stat__val">10+</span><span className="sol-stat__lbl">Ans d'expérience</span></div>
              <div className="sol-stat__sep" />
              <div className="sol-stat"><span className="sol-stat__val">4</span><span className="sol-stat__lbl">Services spécialisés</span></div>
              <div className="sol-stat__sep" />
              <div className="sol-stat"><span className="sol-stat__val">RGE</span><span className="sol-stat__lbl">Certifié QualiPAC</span></div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            INTRO NAVIGATION SERVICES
        ══════════════════════════════════════ */}
        <section className="svc-nav" id="solutions-grid">
          <div className="container">
            <div className="svc-nav__head">
              <div className="svc-label"><span />NOS PRESTATIONS</div>
              <h2 className="svc-nav__title">
                Tout ce dont vous avez besoin,<br />
                <em>en un seul endroit</em>
              </h2>
              <p className="svc-nav__sub">
                Quatre domaines d'expertise, une seule équipe certifiée.
                Découvrez chaque service en détail ci-dessous.
              </p>
            </div>

            <div className="svc-nav__pills">
              {SOLUTIONS.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="svc-nav__pill"
                  style={{ '--c': s.color }}
                >
                  <span className="svc-nav__pill-icon">{s.icon}</span>
                  <span className="svc-nav__pill-num">{s.num}</span>
                  <span className="svc-nav__pill-title">{s.title}</span>
                  <MoveRight size={14} strokeWidth={2} className="svc-nav__pill-arrow" />
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            SERVICES DÉTAILLÉS — layout aéré
        ══════════════════════════════════════ */}
        <section className="svc-section">
          <div className="container">
            {SOLUTIONS.map((s, i) => (
              <ServiceRow key={s.id} s={s} index={i} />
            ))}
          </div>
        </section>

        {/* ══════════════════════════════════════
            MÉTHODE — timeline horizontale
        ══════════════════════════════════════ */}
        <section className="meth-section">
          <div className="container">
            <div className="meth-head">
              <div className="svc-label svc-label--red"><span />NOTRE MÉTHODE</div>
              <h2 className="meth-title">Comment nous travaillons</h2>
            </div>

            <div className="meth-track">
              {STEPS.map((step, i) => (
                <div key={i} className="meth-step">
                  <div className="meth-step__circle">
                    {step.icon}
                    <span className="meth-step__num">{step.num}</span>
                  </div>
                  {i < STEPS.length - 1 && <div className="meth-step__line" />}
                  <h3 className="meth-step__title">{step.title}</h3>
                  <p className="meth-step__text">{step.text}</p>
                </div>
              ))}
            </div>

            <div className="meth-cta">
              <a href="#contact" className="meth-cta__btn">
                Prendre contact <ArrowRight size={16} strokeWidth={2} />
              </a>
              <a href="tel:0618011895" className="meth-cta__tel">
                <Phone size={16} strokeWidth={2} /> 06 18 01 18 95
              </a>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            RGE BAND
        ══════════════════════════════════════ */}
        <section className="rge-band">
          <div className="container">
            <div className="rge-band__inner">
              <img src={logoRGE} alt="Certification RGE QualiPAC" className="rge-band__logo" />
              <div className="rge-band__text">
                <strong>Certifié RGE QualiPAC</strong>
                <p>Accédez à MaPrimeRénov', CEE, éco-PTZ et TVA réduite à 5,5 % grâce à notre certification.</p>
              </div>
              <a href="#contact" className="rge-band__btn">
                En savoir plus <ArrowRight size={14} strokeWidth={2} />
              </a>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════
            FAQ — design ouvert
        ══════════════════════════════════════ */}
        <section className="faq-section">
          <div className="container">
            <div className="faq-layout">

              <div className="faq-sidebar">
                <div className="svc-label"><span />FAQ</div>
                <h2 className="faq-sidebar__title">
                  Questions<br /><em>fréquentes</em>
                </h2>
                <p className="faq-sidebar__sub">
                  Vous ne trouvez pas la réponse ? Contactez-nous directement.
                </p>
                <div className="faq-sidebar__contacts">
                  <a href="tel:0618011895" className="faq-contact-link">
                    <div className="faq-contact-link__icon"><Phone size={16} strokeWidth={2} /></div>
                    <div>
                      <span>Appel direct</span>
                      <strong>06 18 01 18 95</strong>
                    </div>
                  </a>
                  <a href="mailto:soclimate.95@gmail.com" className="faq-contact-link">
                    <div className="faq-contact-link__icon"><Mail size={16} strokeWidth={2} /></div>
                    <div>
                      <span>Email</span>
                      <strong>soclimate.95@gmail.com</strong>
                    </div>
                  </a>
                </div>
              </div>

              <div className="faq-list">
                {FAQ.map((item, i) => (
                  <div
                    key={i}
                    className={`faq-item${openFaq === i ? ' faq-item--open' : ''}`}
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <div className="faq-item__q">
                      <span>{item.q}</span>
                      <ChevronDown size={18} strokeWidth={2} />
                    </div>
                    <div className="faq-item__a">
                      <p>{item.a}</p>
                    </div>
                  </div>
                ))}
              </div>

            </div>
          </div>
        </section>

      </main>
      <Contact />
      <Footer />
    </>
  );
}